/**
 * 사업소(office) 계층형 선택 리스트 공통 로직.
 *
 * 사업소 코드는 자릿수로 계층이 결정된다.
 *   2자리 : 본부      (예: 13 부산광역시상수도사업본부)
 *   4자리 : 사업소     (예: 1304 부산서부사업소)
 *   6자리 : 상수/지하수 (예: 130401 부산서부사업소 상수, 130402 지하수)
 *
 * 하위 코드는 항상 상위 코드로 시작하므로 계층 판정은 prefix 비교로 처리한다.
 * (부산 사업소 코드가 4자리에서 6자리로 확장된 이력이 있어 자릿수를 직접
 *  하드코딩하지 않는다.)
 *
 * 사업소 목록은 API 에 따라 {label, value} 와 {officeName, officeId} 두 형태로
 * 내려오므로, 코드/이름 접근자를 주입받아 양쪽 모두를 지원한다.
 */

/** 부산 본부 코드 */
const BUSAN_HEAD_ID = '13'

/**
 * 전체 조회용 특별 사업소 ID.
 * 실제 하위 사업소를 prefix 로 갖지 않고 백엔드가 전체 조회로 처리하므로,
 * 목록에서는 형제 사업소보다 앞에 고정 노출한다.
 */
const BUSAN_PRIORITY_IDS = ['1399', '1398']

export interface OfficeOption {
  level?: number
  [key: string]: any
}

export type OfficeAccessor = (option: OfficeOption) => string

/** {label, value} / {officeName, officeId} 양쪽을 모두 받아주는 기본 접근자 */
export const defaultGetOfficeId: OfficeAccessor = (o) => String(o?.value ?? o?.officeId ?? '')
export const defaultGetOfficeLabel: OfficeAccessor = (o) => String(o?.label ?? o?.officeName ?? '')

export interface OfficeAccessorOptions {
  getId?: OfficeAccessor
  getLabel?: OfficeAccessor
}

/**
 * 사업소 목록을 계층 순서로 재배열하고 각 항목에 level(0/1/2)을 부여한다.
 *
 * - level 은 리스트 내 최소 코드 길이를 기준으로 동적 계산한다.
 * - 상위(level 0) 사업소는 한글 가나다순, 하위는 코드순으로 정렬한다.
 *   하위 코드가 상위 코드로 시작하므로 코드 문자열 정렬만으로
 *   부모 → 자식 순의 중첩 순서가 만들어진다.
 * - '전체' 항목이 있으면 항상 맨 앞에 둔다.
 *
 * 원본 항목은 그대로 두고 level 만 덧붙이므로, 호출부의 getOptionLabel /
 * onChange 는 기존 필드를 그대로 사용하면 된다.
 */
export const buildHierarchicalOfficeList = (
  list: OfficeOption[],
  {getId = defaultGetOfficeId, getLabel = defaultGetOfficeLabel}: OfficeAccessorOptions = {}
): OfficeOption[] => {
  if (!list) return []
  const allItem = list.find((item) => getLabel(item) === '전체')
  const items = list.filter((item) => getLabel(item) !== '전체')
  if (items.length === 0) return allItem ? [{...allItem, level: 0}] : []

  // 리스트 내 가장 짧은 officeId 길이를 기준으로 level 동적 계산
  const minIdLength = Math.min(...items.map((item) => getId(item).length))
  const getLevel = (id: string) => {
    const diff = id.length - minIdLength
    return diff <= 0 ? 0 : diff <= 2 ? 1 : 2
  }

  const withLevel = items.map((item) => ({...item, level: getLevel(getId(item))}))

  // 상위(level 0) 사업소를 한글 가나다순 정렬
  const parents = withLevel
    .filter((item) => item.level === 0)
    .sort((a, b) => getLabel(a).localeCompare(getLabel(b), 'ko'))

  // 각 부모 아래 자식들을 순서대로 붙임 (부산은 전체 조회용 ID 를 맨 앞에 고정)
  const result: OfficeOption[] = []
  parents.forEach((parent) => {
    result.push(parent)
    const parentId = getId(parent)

    const children = withLevel
      .filter((item) => (item.level ?? 0) > 0 && getId(item).startsWith(parentId))
      .sort((a, b) => {
        const aId = getId(a)
        const bId = getId(b)
        if (parentId === BUSAN_HEAD_ID) {
          const priority = (id: string) => {
            const idx = BUSAN_PRIORITY_IDS.indexOf(id)
            return idx === -1 ? BUSAN_PRIORITY_IDS.length : idx
          }
          const pa = priority(aId)
          const pb = priority(bId)
          if (pa !== pb) return pa - pb
        }
        return aId.localeCompare(bId)
      })

    result.push(...children)
  })

  return allItem ? [{...allItem, level: 0}, ...result] : result
}

/**
 * 해당 사업소가 펼칠 하위(level 2) 항목을 가지고 있는지 여부.
 * 전체 조회용 특별 ID(1399/1398)는 하위를 prefix 로 갖지 않아 false 가 된다.
 */
export const hasChildOffices = (
  list: OfficeOption[],
  id: string,
  getId: OfficeAccessor = defaultGetOfficeId
): boolean => (list || []).some((o) => o.level === 2 && getId(o).startsWith(id))

/**
 * MUI Autocomplete 의 filterOptions 로 넘길 필터를 생성한다.
 *
 * - 검색어가 있으면 매칭된 항목의 상위/하위를 함께 노출해 계층이 끊기지 않게 한다.
 * - 검색어가 없으면 펼쳐진 그룹의 하위(level 2)만 노출한다.
 *
 * @param expandedGroups 펼쳐진 부모(level 1) 사업소 id 집합
 */
export const createOfficeOptionFilter =
  (
    expandedGroups: Set<string>,
    {getId = defaultGetOfficeId, getLabel = defaultGetOfficeLabel}: OfficeAccessorOptions = {}
  ) =>
  (options: OfficeOption[], state: {inputValue: string}): OfficeOption[] => {
    if (state.inputValue) {
      const q = state.inputValue.toLowerCase()
      const matched = options.filter((o) => getLabel(o).toLowerCase().includes(q))
      const include = new Set<string>(matched.map((o) => getId(o)))
      matched.forEach((o) => {
        const id = getId(o)
        // 자식 → 상위 부모들 모두 포함 (level 0, level 1)
        if ((o.level ?? 0) > 0) {
          options.forEach((p) => {
            if ((p.level ?? 0) < (o.level ?? 0) && id.startsWith(getId(p))) include.add(getId(p))
          })
        }
        // 대분류 매칭 → 하위 전체 포함
        if (o.level === 0) {
          options.forEach((c) => {
            if (getId(c).startsWith(id)) include.add(getId(c))
          })
        }
        // 중분류 매칭 → 소분류 자식도 포함
        if (o.level === 1) {
          options.forEach((c) => {
            if (c.level === 2 && getId(c).startsWith(id)) include.add(getId(c))
          })
        }
      })
      return options.filter((o) => include.has(getId(o)))
    }
    // 부모(level 1) id 자체를 prefix 로 비교 — 사업소 코드 자릿수(4/6자리)에 무관
    return options.filter((o) => {
      if (o.level !== 2) return true
      const id = getId(o)
      return Array.from(expandedGroups).some((p) => id.startsWith(p))
    })
  }
